import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Row, Col, Card, Table, Button, Input, Modal, Typography, Tag, Space,
  Drawer, Form, Select, notification, Divider,
} from "antd";
import { DeleteOutlined, EditOutlined, ExclamationCircleOutlined, PlusOutlined, SaveOutlined } from "@ant-design/icons";
import axios from "axios";
import { baseUrl } from "../../config";

const { confirm } = Modal;
const { Search } = Input;
const { Title } = Typography;
const { Item } = Form;
const { Option } = Select;

function PopCountryList() {
  const history = useHistory();
  const [form] = Form.useForm();

  const [dataList, setDataList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(10);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const authHeader = { Authorization: `Bearer ${localStorage.getItem('accessToken')}` };

  const fetchList = async (page = currentPage, searchVal = search) => {
    setLoading(true);
    try {
      const res = await axios.get(`${baseUrl}/pop-country`, {
        headers: authHeader,
        params: { page, limit, search: searchVal },
      });
      if (res.data.status) {
        setDataList(res.data.result.map((item, i) => ({ ...item, key: (page - 1) * limit + i + 1 })));
        setTotalCount(res.data.totalCount);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchList(); }, [currentPage]);

  const openAdd = () => {
    form.resetFields();
    form.setFieldsValue({ status: '1' });
    setEditId(null);
    setDrawerOpen(true);
  };

  const openEdit = (record) => {
    form.setFieldsValue({ name: record.name, country_code: record.country_code, status: String(record.status) });
    setEditId(record.id);
    setDrawerOpen(true);
  };

  const onSave = async (values) => {
    setSaving(true);
    try {
      if (editId) {
        await axios.put(`${baseUrl}/pop-country/${editId}`, values, { headers: authHeader });
      } else {
        await axios.post(`${baseUrl}/pop-country`, values, { headers: authHeader });
      }
      notification.success({ message: editId ? 'Country updated successfully' : 'Country added successfully' });
      setDrawerOpen(false);
      fetchList();
    } catch (err) {
      notification.error({ message: err.response?.data?.message || 'Error saving record' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (id) => {
    confirm({
      title: 'Are you sure you want to delete this record?',
      icon: <ExclamationCircleOutlined />,
      okText: 'Yes, Delete', okType: 'danger', cancelText: 'Cancel',
      onOk: async () => {
        try {
          await axios.delete(`${baseUrl}/pop-country/${id}`, { headers: authHeader });
          notification.success({ message: 'Deleted successfully' });
          fetchList();
        } catch (err) { console.error(err); }
      }
    });
  };

  const columns = [
    { title: '#', dataIndex: 'key', key: 'key', width: 60 },
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Country Code', dataIndex: 'country_code', key: 'country_code', width: 120 },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 90,
      render: s => <Tag color={String(s) === '1' ? 'green' : 'red'}>{String(s) === '1' ? 'Active' : 'Inactive'}</Tag>
    },
    {
      title: 'Actions', key: 'actions', fixed: 'right', width: 150,
      render: (_, record) => (
        <Space size={4}>
          <Button
            size="small"
            icon={<EditOutlined />}
            style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff' }}
            onClick={() => openEdit(record)}
          >Edit</Button>
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          >Delete</Button>
        </Space>
      )
    }
  ];

  return (
    <div className="tabled">
      <Row gutter={[24, 0]}>
        <Col span={24}>
          <Card
            title={<span style={{ color: '#1e293b', fontWeight: 700, fontSize: 15 }}>Country List</span>}
            headStyle={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0' }}
            extra={
              <Button
                icon={<PlusOutlined />}
                style={{ background: '#fff', borderColor: '#fff', color: '#7a0000', fontWeight: 600 }}
                onClick={openAdd}
              >Add New</Button>
            }
          >
            <div style={{ marginBottom: 16 }}>
              <Search
                placeholder="Search country..."
                onSearch={val => { setSearch(val); setCurrentPage(1); fetchList(1, val); }}
                allowClear
                style={{ width: 300 }}
                enterButton={
                  <Button style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff' }}>Search</Button>
                }
              />
            </div>
            <Table
              columns={columns}
              dataSource={dataList}
              loading={loading}
              pagination={{
                current: currentPage,
                pageSize: limit,
                total: totalCount,
                onChange: p => setCurrentPage(p),
                showTotal: t => `Total ${t} countries`
              }}
              scroll={{ x: 'max-content' }}
              size="small"
            />
          </Card>
        </Col>
      </Row>

      <Drawer
        title={
          <span style={{ fontWeight: 700, color: '#7a0000' }}>
            {editId ? 'Edit Country' : 'Add Country'}
          </span>
        }
        width={450}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        bodyStyle={{ paddingBottom: 80 }}
        footer={
          <Space>
            <Button
              icon={<SaveOutlined />}
              style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff' }}
              onClick={() => form.submit()}
              loading={saving}
            >Save</Button>
            <Button onClick={() => setDrawerOpen(false)}>Cancel</Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical" onFinish={onSave}>
          <Divider orientation="left" style={{ color: '#7a0000', fontWeight: 700, fontSize: 13, borderColor: '#d0dce8', margin: '16px 0 8px' }}>Country Details</Divider>
          <Item name="name" label="Name" rules={[{ required: true, message: 'Required' }]}>
            <Input placeholder="Enter country name" />
          </Item>
          <Item name="country_code" label="Country Code">
            <Input placeholder="e.g. IN, US" />
          </Item>
          <Divider orientation="left" style={{ color: '#7a0000', fontWeight: 700, fontSize: 13, borderColor: '#d0dce8', margin: '16px 0 8px' }}>Settings</Divider>
          <Item name="status" label="Status">
            <Select>
              <Option value="1">Active</Option>
              <Option value="0">Inactive</Option>
            </Select>
          </Item>
        </Form>
      </Drawer>
    </div>
  );
}

export default PopCountryList;
